import React, { useEffect, useRef, useState } from 'react';
import { FaTimes, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import chatService from '../../services/chatService';
import './TalkAttachmentModals.css';

const SCRIPT_ID = 'onlyoffice-docs-api-talk';

const detectDocumentType = (ext) => {
    const e = (ext || '').toLowerCase();
    if (['doc', 'docx', 'odt', 'rtf', 'txt'].includes(e)) return 'word';
    if (['xls', 'xlsx', 'ods', 'csv'].includes(e)) return 'cell';
    if (['ppt', 'pptx', 'odp'].includes(e)) return 'slide';
    return null;
};

const TalkAttachmentOnlyOfficeViewerModal = ({ conversationId, messageId, upload, onClose }) => {
    const editorRef = useRef(null);
    const destroyTimerRef = useRef(null);
    const [status, setStatus] = useState('loading');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!upload) return;

        setStatus('loading');
        setErrorMsg('');

        let cancelled = false;
        let docEditor = null;

        const cleanup = () => {
            cancelled = true;
            if (destroyTimerRef.current) clearTimeout(destroyTimerRef.current);
            if (docEditor) {
                const editorToDestroy = docEditor;
                docEditor = null;
                try { editorToDestroy.destroyEditor(); } catch (_) {}
            }
            const existingScript = document.getElementById(SCRIPT_ID);
            if (existingScript) existingScript.remove();
        };

        const init = async () => {
            try {
                const res = await chatService.getAttachmentOnlyOfficeConfig(conversationId, messageId, upload.id);
                if (cancelled) return;

                if (!res?.status) {
                    setErrorMsg('Failed to load document configuration.');
                    setStatus('error');
                    return;
                }

                const config = res.data;
                const docType = detectDocumentType(config.fileType);
                if (!docType) {
                    setErrorMsg(`Unsupported file type: .${config.fileType}`);
                    setStatus('error');
                    return;
                }

                const serverUrl = process.env.REACT_APP_ONLYOFFICE_SERVER_URL || 'http://localhost:8081';
                const scriptSrc = `${serverUrl}/web-apps/apps/api/documents/api.js`;

                await new Promise((resolve, reject) => {
                    if (document.getElementById(SCRIPT_ID)) { resolve(); return; }
                    const script = document.createElement('script');
                    script.id = SCRIPT_ID;
                    script.src = scriptSrc;
                    script.onload = resolve;
                    script.onerror = () => reject(new Error('Failed to load OnlyOffice DocsAPI script. Is the server running?'));
                    document.head.appendChild(script);
                });

                if (cancelled) return;
                if (!window.DocsAPI) throw new Error('DocsAPI not available after script load.');

                docEditor = new window.DocsAPI.DocEditor('talk-onlyoffice-editor-container', {
                    document: {
                        fileType: config.fileType,
                        key: config.key,
                        title: config.title,
                        url: config.documentUrl,
                        permissions: { edit: false, download: true },
                    },
                    editorConfig: {
                        mode: 'view',
                        customization: {
                            hideRightMenu: true,
                            features: { tabStyle: 'line', tabBackground: 'white' },
                        },
                    },
                    documentType: docType,
                    height: '100%',
                    width: '100%',
                    events: {
                        onAppReady: () => { if (!cancelled) setStatus('ready'); },
                        onError: (event) => {
                            if (!cancelled) {
                                setErrorMsg(event?.data?.errorDescription || 'An error occurred in the document viewer.');
                                setStatus('error');
                            }
                        },
                    },
                });

                editorRef.current = docEditor;
            } catch (err) {
                if (!cancelled) {
                    setErrorMsg(err.message || 'Unexpected error loading document.');
                    setStatus('error');
                }
            }
        };

        init();
        return cleanup;
    }, [conversationId, messageId, upload]);

    useEffect(() => {
        if (!upload) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [upload, onClose]);

    if (!upload) return null;

    return (
        <div
            className="talk-attach-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="dialog"
            aria-modal="true"
            aria-label={`Viewing ${upload.name}`}
        >
            <div className="talk-attach-modal">
                <div className="talk-attach-header">
                    <span className="talk-attach-title" title={upload.name}>{upload.name}</span>
                    <button className="talk-attach-close-btn" onClick={onClose} aria-label="Close document viewer">
                        <FaTimes />
                    </button>
                </div>

                <div className="talk-attach-body">
                    <div
                        id="talk-onlyoffice-editor-container"
                        className={`talk-attach-oo-container ${status === 'ready' ? 'talk-attach-oo-container--ready' : ''}`}
                    />

                    {status === 'loading' && (
                        <div className="talk-attach-state-overlay">
                            <FaSpinner className="talk-attach-spinner" />
                            <p>Loading document…</p>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="talk-attach-state-overlay">
                            <FaExclamationTriangle className="talk-attach-error-icon" />
                            <p>{errorMsg}</p>
                            <button className="talk-attach-retry-btn" onClick={onClose}>Close</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TalkAttachmentOnlyOfficeViewerModal;
